import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AngularFirestore, AngularFirestoreCollection, AngularFirestoreDocument } from 'angularfire2/firestore';
import { FirestoreService } from './firestore/firestore.service';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/combineLatest';
import 'rxjs/add/operator/mergeMap';

import { Globals, Ref } from '../globals';
import { firestore } from 'firebase/app';
import { CustomerBillId, CreditNote, BillFile } from './customer-bill.service';
import { EmployeeId } from './employee.service';
import { Utils } from '../util';

export interface Income extends BillFile {
  active: number;
  createdAt: any;
  updatedAt: any;

  customerRef: any;
  fiscalDataRef: any;
  bankRef: any;
  employeeRef: Ref;
  
  folio: string;
  date: string;
  amount: number;
  extraAmount: number;
  payMethod: keyof IncomeData['statusObjects'];
  reference: string;
  notes: string;
  status: string;
  uuid: string;
  _stampedByMedicosproBackendServer?: boolean;

  bills: IncomeBill[];
  uuidHistory: {[uuid: string]: UuidHistory};

  folioLogs?: FolioLog[];
}

export interface IncomeId extends Income {
  id: string;
  customerObj: any;
  fiscalDataObj: any;
  bankObj: any;
  employeeObj?: EmployeeId;
}

export interface IncomeBill {
  customerBillId: string;
  amount: number;
  extraAmount: number;
}

export interface UuidHistory extends BillFile{
  date: string;
}

export interface FolioLog{
  lastFolio: number;
  newFolio: number;
  datetime: string;
  lastUuid: string;
  userFullName: string;
}

@Injectable()
export class IncomeService {

  incomeCollection: AngularFirestoreCollection<Income>;
  incomes: any;

  incomeDocument: AngularFirestoreDocument<IncomeId>;
  income: Observable<IncomeId>;

  collection: string;
  customerCollection: string;
  fiscalDataCollection: string;
  bankCollection: string;
  employeeCollection: string;

  // NOTE: Change this any for Income Interface
  income$: Observable<any>;
  incomes$: Observable<any[]>;

  constructor(
    private afs: AngularFirestore,
    private afh: FirestoreService,
    private router: Router
  ) {
    this.collection = Globals.INCOME_COLLECTION;
    this.customerCollection = Globals.CUSTOMER_SUPPLIER_COLLECTION;
    this.fiscalDataCollection = Globals.FISCAL_DATA_COLLECTION;
    this.bankCollection = Globals.BANK_COLLECTION;
    this.employeeCollection = Globals.EMPLOYEE_COLLECTION;
  }

  /**
   * Get all incomes with references
   * @return {Observable} Arrays of incomes
   */
  getIncomes({year='', onEmpty=null}={}) {
    let col;

    if(year){
      let s = `${year}-00-00`, e = `${year}-99-99`;
      col = this.afh.colWithIds$(this.collection, ref => ref.orderBy('date','desc').where('date', '>', s).where('date', '<', e).where('active', '==', true) );
    }else{
      col = this.afh.colWithIds$(this.collection, ref => ref.orderBy('date','desc').where('active', '==', true) );
    }

    return col.map(documents => {
      if(!documents.length && onEmpty) onEmpty();
      return documents.map(document => {
        let hasEmp = !! document.employeeRef;
        let customerRef = this.afh.docWithIds$(`${document.customerRef.path}`);
        let employeeRef = hasEmp ? this.afh.docWithIds$(`${document.employeeRef.path}`) : customerRef;
        return Observable.combineLatest(
          customerRef,
          this.afh.docWithIds$(`${document.fiscalDataRef.path}`),
          this.afh.docWithIds$(`${document.bankRef.path}`),
          employeeRef,
          (customerData, fiscalDataData, bankData, employee) => ({ customer: customerData, fiscalData: fiscalDataData, bank: bankData, employee })
        ).map( data => ({ customerObj: data.customer, fiscalDataObj: data.fiscalData, bankObj: data.bank, employeeObj: hasEmp ? data.employee : undefined, ...document }) );
      });
    }).mergeMap(documents => Observable.combineLatest(documents));
  }

  /**
   * Get all incomes with references within 15 days
   * @return {Observable} Arrays of incomes
   */
  getIncomesFifteenDays() {
    let filteredDate = new Date();
    filteredDate.setDate(filteredDate.getDate() - 15);
    let date = this.stringifyDate(filteredDate);
    return this.afh.colWithIds$(this.collection, ref => ref.orderBy('date','desc').where('date', '>', date).where('active', '==', true) ).map(documents => {
      return documents.map(document => {
        let hasEmp = !! document.employeeRef;
        let customerRef = this.afh.docWithIds$(`${document.customerRef.path}`);
        let employeeRef = hasEmp ? this.afh.docWithIds$(`${document.employeeRef.path}`) : customerRef;
        return Observable.combineLatest(
          customerRef,
          this.afh.docWithIds$(`${document.fiscalDataRef.path}`),
          this.afh.docWithIds$(`${document.bankRef.path}`),
          employeeRef,
          (customerData, fiscalDataData, bankData, employee) => ({ customer: customerData, fiscalData: fiscalDataData, bank: bankData, employee })
        ).map( data => ({ customerObj: data.customer, fiscalDataObj: data.fiscalData, bankObj: data.bank, employeeObj: hasEmp ? data.employee : undefined, ...document }) );
      });
    }).mergeMap(documents => Observable.combineLatest(documents));
  }

  /**
  * Get income data
  * @param {string} sid - The id of the income
  * @return {Observable} The Income Object
  */
  getIncome(sid: string) {
    return this.income$ = this.afh.docWithIds$(this.collection + '/' + sid).map(document => {
      let hasEmp = !! document.employeeRef;
      let customerRef = this.afh.docWithIds$(`${document.customerRef.path}`);
      let employeeRef = hasEmp ? this.afh.docWithIds$(`${document.employeeRef.path}`) : customerRef;
      return Observable.combineLatest(
        customerRef,
        this.afh.docWithIds$(`${document.fiscalDataRef.path}`),
        this.afh.docWithIds$(`${document.bankRef.path}`),
        employeeRef,
        (customerData, fiscalDataData, bankData, employee) => ({ customer: customerData, fiscalData: fiscalDataData, bank: bankData, employee })
      ).map( data => ({ customerObj: data.customer, fiscalDataObj: data.fiscalData, bankObj: data.bank, employeeObj: hasEmp ? data.employee : undefined, ...document }) );

    }).mergeMap(documents => Observable.combineLatest(documents));
  }

  /**
  * Create new income
  * @param {Income} income - The data of the income
  */
  addIncome({employeeRef, ...income}: Income) {
    income.customerRef = this.afh.doc(`${this.customerCollection}/${income.customerRef}`).ref;
    income.fiscalDataRef = this.afh.doc(`${this.fiscalDataCollection}/${income.fiscalDataRef}`).ref;
    income.bankRef = this.afh.doc(`${this.bankCollection}/${income.bankRef}`).ref;
    if(employeeRef) employeeRef = this.afh.doc(`${this.employeeCollection}/${employeeRef}`).ref;
    
    const docId = this.afs.createId();
    const ref = this.afs.collection(this.collection).doc(docId);
    ref.set({active: true, createdAt: `${Utils.today}T${Utils.time}`, employeeRef, ...income});
    return docId;
  }

  /**
   * Update current income
   * @param {string} sid  - The id of the income
   * @param {Income} income - The data of the income
   */
  updateIncome(sid: string, {employeeRef, ...income}: Income) {
    income.customerRef = this.afh.doc(`${this.customerCollection}/${income.customerRef}`).ref;
    income.fiscalDataRef = this.afh.doc(`${this.fiscalDataCollection}/${income.fiscalDataRef}`).ref;
    income.bankRef = this.afh.doc(`${this.bankCollection}/${income.bankRef}`).ref;
    if(employeeRef) employeeRef = this.afh.doc(`${this.employeeCollection}/${employeeRef}`).ref;
    this.afh.update(`${this.collection}/${sid}`, {employeeRef, ...income});
  }
  updateByField(obj, id){
    console.log(`Se actualizó este documento: ${this.collection}/${id}`);
    console.log(obj)
    return this.afh.update(`${this.collection}/${id}`, obj);
  }

  /**
   * Delete current income
   * @param {string} sid - The id of the income
   */
  deleteIncome(sid: string) {
    this.afh.delete(`${this.collection}/${sid}`);
  }

  windowReplace(action: string, id?: string){
    switch(action){
      case 'add':
      case 'create':
        this.router.navigate([`/new_income/${id}`]);
        break;
      case 'edit':
      case 'update':
        this.router.navigate([`/edit_income/${id}`]);
        break;
      case 'view':
      case 'display':
        this.router.navigate([`/vw_income/${id}`]);
        break;
      case 'read':
        this.router.navigate([`/income`]);
        break;
      case 'billView':
        this.router.navigate([`/vw_customerBill/${id}`]);
        break;
      default:
        console.log('Not an action');
    }
  }


  get today(){
    return this.stringifyDate(new Date());
  }
  stringifyDate(date: Date){
    var dd:string = date.getDate() + '';
    var mm:string = date.getMonth()+ 1 + '';
    var yyyy = date.getFullYear();
    if(+dd<10) dd = '0'+dd;
    if(+mm<10) mm = '0'+mm;
    return yyyy + '-' + mm + '-' + dd;
  }
}

export class IncomeData {
  //IN CASE OF ORDER CHANGE,
  //DONT CHANGE KEYS (USED FOR DATABASE), CHANGE INDEX ATTRIBUTE
  statusObjects = {
    '001': { index: '01', name: 'Efectivo', short: 'Efectivo' },
    '002': { index: '02', name: 'Cheque nominativo', short: 'Cheque' },
    '003': { index: '03', name: 'Transferencia electrónica de fondos', short: 'Transfer' },
    '004': { index: '04', name: 'Tarjeta de crédito', short: 'TdC' },
    '005': { index: '28', name: 'Tarjeta de débito', short: 'TdD' },
    '999': { index: '99', name: 'Por definir', short: 'TBD' },
  };// as const;
}

export class IncomedBill {
  
  customerBill: CustomerBillId;
  lastAmount: number;
  lastExtraAmount: number;
  amount: number;
  extraAmount: number = 0;

  constructor({status='key0', ...customerBill}: CustomerBillId, incomeId?: string){
    this.customerBill = {status, ...customerBill};
    let incomed = incomeId ?  customerBill.incomeds.find(incomed => incomed.incomeRef.id == incomeId) : undefined;
    this.lastAmount = incomed ? incomed.amount : 0;
    this.lastExtraAmount = incomed && incomed.extraAmount || 0;
    this.amount = this.lastAmount;
    this.extraAmount = this.lastExtraAmount;
  }

  get isValid(): boolean { return !this.isOver && this.amount >= 0 }

  get isComplete(): boolean {
    //if(!!(!this.customerBill.extraFiscalDataId ? Utils.money(this.residue) == '0.00' : this.residue <= this.billTotal*(10/100))) console.log('complete');
    //else console.log(this.customerBill.id, this.folio);
    return !this.customerBill.extraFiscalDataId ? Utils.money(this.residue) == '0.00' : this.residue <= 1//this.billTotal*(10/100); // This makes little "saldos" to not appear.
    //return Utils.money(this.residue) == '0.00' || Math.abs(this.residue) < 1;
  }
  get isUnder(): boolean { return !this.isComplete && this.residue > 0; }
  get isOver(): boolean { return !this.isComplete && this.residue < 0; }

  get databaseObject(): IncomeBill { return {customerBillId: this.customerBill.id, amount: this.amount, extraAmount: this.extraAmount}; }

  get folio(): number { return this.customerBill.folio; }
  get status(): string { return Globals.BILL_STATUS[this.customerBill.status] }
  get creditNotes(): CreditNote[] { return this.customerBill.creditNotes; }
  get incomeIds(): string[] { return this.customerBill.incomeds.map(({incomeRef: {id}}) => id); }

  get billTotal(): number { return this.customerBill.totals.total; }
  get creditNotesTotal(): number { return (this.customerBill.creditNotes || []).reduce((a, c) => a + (c.status < 'key97' ? c.total : 0), 0) }
  get otherIncomesTotal(): number { return this.incomedTotal - this.lastAmount; }
  get otherIncomesExtraTotal(): number { return this.incomedExtraTotal - this.lastExtraAmount; }
  get total(): number { return this.billTotal - (this.creditNotesTotal + this.otherIncomesTotal + this.otherIncomesExtraTotal); }
  get incomedTotal(): number { return this.customerBill.incomeds.reduce( (total, incomed) => total + incomed.amount, 0); }
  get incomedExtraTotal(): number { return this.customerBill.incomeds.reduce( (total, incomed) => total + incomed.extraAmount, 0) || 0; }

  get residue(): number { let residue = this.total - this.amount - this.extraAmount; return Utils.strToNumber(Utils.money(residue)); }
}

export class IncomeFilter {
  allRows: any[];

  dateIndex: number;
  customerIndex: number;
  bankNumberIndex: number;

  constructor(allRows: any[], {dateIndex=0, customerIndex=0, bankNumberIndex=0}){
    this.allRows = allRows;
    this.dateIndex = dateIndex;
    this.customerIndex = customerIndex;
    this.bankNumberIndex = bankNumberIndex;

    let customers = new Set( allRows.map(row => row.customerObj.name) );
    let sortedCustomers = [...customers].sort((it1, it2) => it1 > it2 ? 1 : -1);
    this.customerSelect = ['Todos', ...sortedCustomers].map(name => ({name}) );

    let bankNumbers = new Set( allRows.map(row => row.bankNumber) );
    let sortedBankNumbers = [...bankNumbers].sort((it1, it2) => it1 > it2 ? 1 : -1);
    this.bankNumberSelect = ['Todas', ...sortedBankNumbers].map(name => ({name}) );
  }

  get filteredData() {
    return this.allRows
      .filter(({date}) => {
        let rowDate = new Date(date);
        let filterDate = new Date();
        let {amount, unit} = this.dateSelect[this.dateIndex];
        switch(unit){
          case 'day': filterDate.setDate(filterDate.getDate() - amount); break;
          case 'month': filterDate.setMonth(filterDate.getMonth() - amount); filterDate.setDate(0); break;
          case 'year': filterDate = new Date(filterDate.getFullYear() - amount, 0); break;
          default: filterDate.setFullYear(2015); break;
        }
        if(rowDate >= filterDate) return 1;
      })
      .filter(({customerName: rowName}) => {
        let {name: filterName} = this.customerSelect[this.customerIndex];
        if(rowName == filterName || filterName == 'Todos') return 1;
      })
      .filter(({bankNumber: rowName}) => {
        let {name: filterName} = this.bankNumberSelect[this.bankNumberIndex];
        if(rowName == filterName || filterName == 'Todas') return 1;
      });
  }

  get json(): string {
    return JSON.stringify({
      dateIndex: this.dateIndex,
      customerIndex: this.customerIndex,
      bankNumberIndex: this.bankNumberIndex
    });
  }

  dateSelect = [
    { amount: 15, unit: 'day', name: '15 días' },
    { amount: 0, unit: 'month', name: 'Este mes' },
    { amount: 6, unit: 'month', name: '6 meses' },
    { amount: 0, unit: 'year', name: 'Este año' },
    { amount: 0, unit: 'forever', name: 'Siempre' },
  ];
  customerSelect = [];
  bankNumberSelect = [];
}
